import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, ResourceRef, signal, WritableSignal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { map, Observable, of, tap } from 'rxjs';
import { DialogManager } from '../../../global/dialog-manager/dialog';
import { Loader } from '../../../global/loader';
import { ApiRouter } from '../../../global/routes/api-routes';
import { CancelBookingComponent } from '../components/claims-tracker/components/cancel-booking/cancel-booking.component';
import { GetHistoryResponse, GetPlanDetailsResponse, GetRepairResponse } from '../mmr.types';
import { ClaimDetailsPanelResumeBooking, ClaimType } from '../types/mmr-claims.type';
import { setupErrorRetryLogic } from '../utils/error-retry';

export type LoadPlanClaimsObject = {
  plan: ResourceRef<GetPlanDetailsResponse>;
  claims: ResourceRef<GetRepairResponse>;
  claimsLastUpdated: WritableSignal<Date | undefined>;
  history: ResourceRef<GetHistoryResponse>;
  // planSlowLoadTriggered: WritableSignal<boolean>;
  // repairSlowLoadTriggered: WritableSignal<boolean>;
  historySlowLoadTriggered: WritableSignal<boolean>;
};

@Injectable({
  providedIn: 'root',
})
export class MmrService {
  planNumber: WritableSignal<string | undefined> = signal(undefined);

  loadHistory = signal(false);

  planClaimsDetails: LoadPlanClaimsObject = {
    plan: this.getPlan(),
    // planSlowLoadTriggered: signal(false),
    claims: this.getClaimInformation(),
    claimsLastUpdated: signal<Date | undefined>(undefined),
    // repairSlowLoadTriggered: signal(false),
    history: this.getHistory(),
    historySlowLoadTriggered: signal(false),
  };

  constructor(
    private readonly injector: Injector,
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  loadPlanClaims(planNumber: string) {
    this.planNumber.set(planNumber);
  }

  navigateToOrbitResume(claimType: 'repair' | 'service', claimValue: ClaimDetailsPanelResumeBooking): void {
    const payload = {
      claimId: claimValue.id,
      claimType: claimType.toUpperCase(),
      claimTypeId: claimValue.claimTypeId,
      currentState: claimValue.nextActionState,
      manufacturer: this.planClaimsDetails.claims.value()?.itemDetails?.manufacturer,
      planNumber: this.planNumber(),
    };
    this.router.navigate(['/', this.planNumber(), 'orbit'], {
      queryParams: { new_session: JSON.stringify(payload) },
    });
  }

  // reloadRepair(): void {
  //   this.planClaimsDetails.repair.reload();
  // }
  //
  // reloadHistory(): void {
  //   this.planClaimsDetails.history.reload();
  // }
  //

  navigateToStartClaim(
    claimType: 'repair' | 'service',
    claimValue: ClaimType,
    hasServiceOption: boolean,
    productType?: string
  ) {
    const payload = {
      claimType: claimType.toUpperCase(),
      claimTypeId: claimValue.claimTypeID,
      currentState: claimType === 'repair' ? 'REQUEST_REPAIR' : 'REQUEST_SERVICE',
      manufacturer: this.planClaimsDetails.claims.value()?.itemDetails?.manufacturer,
      planNumber: this.planNumber(),
      ...(productType && productType.trim() !== '' && { selectedProductType: productType }),
      hasServiceOption: hasServiceOption,
    };
    this.router.navigate(['/', this.planNumber(), 'orbit'], {
      queryParams: { new_session: JSON.stringify(payload) },
    });
  }

  private getPlan(): ResourceRef<any> {
    return rxResource({
      params: () => this.planNumber(),
      stream: ({ params }) => {
        if (params === undefined) return of({});
        return this.http.get(ApiRouter.getPlanDetails(params));
      },
      injector: this.injector,
    });
  }

  private getClaimInformation(): ResourceRef<any> {
    return rxResource({
      params: () => this.planNumber(),
      stream: ({ params }) => {
        // this.planClaimsDetails.repairSlowLoadTriggered.set(false);
        if (params === undefined) return of({});

        const lastUpdatedTime = new Date();
        return setupErrorRetryLogic(
          this.http.get<any>(ApiRouter.getPlanClaims(params)).pipe(
            map((value) => {
              value['lastUpdated'] = lastUpdatedTime;
              return value;
            })
          ),
          {
            retryDelayBase: 1000,
            count: 1,
            slowMessageHandler: () => {
              // this.planClaimsDetails.repairSlowLoadTriggered.set(true);
            },
          }
        );
      },
      injector: this.injector,
    });
  }

  private getHistory(): ResourceRef<any> {
    return rxResource({
      params: () => ({ shouldLoadHistory: this.loadHistory(), planNumber: this.planNumber() }),
      stream: ({ params }) => {
        this.planClaimsDetails.historySlowLoadTriggered.set(false);
        if (params === undefined || !params.shouldLoadHistory || !params.planNumber) return of({});

        return setupErrorRetryLogic(this.http.get(ApiRouter.getPlanHistory(params.planNumber)), {
          retryDelayBase: 1000,
          count: 1,
          slowMessageHandler: () => {
            this.planClaimsDetails.historySlowLoadTriggered.set(true);
          },
        });
      },
      injector: this.injector,
    });
  }

  cancelClaim(repairId: string, rms: 'ORBIT' | 'BRIGHT'): Observable<void> {
    return this.http.delete<void>(ApiRouter.cancelClaim(this.planNumber() as string, repairId, rms), {}).pipe(
      tap(() => {
        this.planClaimsDetails.claims.reload();
      })
    );
  }

  performClaimCancel(repairId: string, rms: 'ORBIT' | 'BRIGHT'): void {
    const dialogAndRetry = (id: string) => {
      DialogManager.openDialog(CancelBookingComponent, {
        data: { state: 'cancelError', repairId: id },
      }).closed.subscribe((retry) => {
        if (retry) {
          execute(id);
        }
      });
    };

    const execute = (id: string) => {
      Loader.showLoader();
      this.cancelClaim(id, rms).subscribe({
        next: () => {
          Loader.hideLoader();
          this.showCancellationDialog('cancelComplete');
        },
        error: () => {
          Loader.hideLoader();
          dialogAndRetry(id);
        },
      });
    };

    DialogManager.openDialog(CancelBookingComponent, {
      data: { repairId },
    }).closed.subscribe((confirmed) => {
      if (confirmed) {
        execute(repairId);
      }
    });
  }

  showCancellationDialog(state: string): void {
    DialogManager.openDialog(CancelBookingComponent, {
      data: {
        state,
      },
    });
  }
}
